import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { type PausedStage } from '@/types/enums';
import { StageRichBody } from './StageRichBody';
import { RawOutputFallback } from './RawOutputFallback';
import { isRichRenderEmpty } from './richEmpty';
import { fieldFrom, formatValue } from './StageReviewBits';

/**
 * Read-only rendering of a paused stage's agent output.
 *
 * Split out of StageReviewPanel on 2026-07-16 for CC-10 (the panel was 387
 * lines against a 300 cap). The seam is deliberate: everything here is
 * display-only and derives purely from `output`, while the panel keeps the
 * review actions and the state they mutate.
 */

interface OutputProps {
  stage: PausedStage;
  output: Record<string, unknown>;
  jobId: string;
  summaryKeys: string[];
  onActivity?: () => void;
}

export function StageOutputSections({
  stage,
  output,
  jobId,
  summaryKeys,
  onActivity,
}: OutputProps) {
  const hasNarrative =
    typeof output.narrative === 'string' && output.narrative.trim().length > 0;
  const richEmpty = isRichRenderEmpty(stage, output);

  return (
    <>
      {/* Truncation warning — fires when the LLM hit max_tokens and
          our salvage parser only recovered a partial response. The
          data below is real; there's just less of it than the agent
          would have produced with a higher output budget. */}
      {(output._truncated === true ||
        output._pydantic_validation_failed === true ||
        output._backfilled_from_narrative === true) && (
        <Alert variant="destructive" className="border-2 border-amber-400 bg-amber-50 text-amber-900 dark:border-amber-600 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription className="text-sm">
            <span className="font-bold">Agent response was truncated.</span>{' '}
            The LLM hit its <code className="font-mono">max_tokens</code> limit
            mid-stream. CMP salvaged every complete item before the cutoff,
            but later items are missing. Raise <code className="font-mono">max_tokens</code>{' '}
            on the Kaycoo agent definition (or switch to a model with a
            larger output budget) to capture the full response on the next
            run.{' '}
            {output._backfilled_from_narrative === true && (
              <span className="block mt-1 text-xs">
                This row was backfilled from the raw narrative
                {typeof output._backfilled_at === 'string'
                  ? ` on ${output._backfilled_at}`
                  : ''}
                .
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Rich stage-specific body — primary surface for the reviewer.
          For every stage we now have a dedicated component (Document,
          Parties, Clause, Gate 1, Compliance, Signature, Gate 2, Final
          Gate, Bootstrap). */}
      <StageRichBody stage={stage} output={output} jobId={jobId} onActivity={onActivity} />

      {/* The agent's own narrative — typically a markdown / JSON report
          from Kaycoo. Auto-opens when the structured Rich*Body would be
          blank (agent renamed a field, prompt schema changed) so the
          reviewer always sees the agent's actual output. Stays collapsed
          when the structured cards already have content. */}
      {hasNarrative && (
        <details className="text-xs" open={richEmpty}>
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
            {richEmpty && (
              <AlertCircle className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
            )}
            <span>
              {richEmpty
                ? "CMP didn't recognise this output shape — showing agent's raw response"
                : 'Show raw agent report'}
            </span>
          </summary>
          <pre className="mt-2 max-h-[600px] overflow-auto rounded border bg-muted/30 p-3 text-sm whitespace-pre-wrap font-sans leading-relaxed">
            {output.narrative as string}
          </pre>
        </details>
      )}

      {/* Raw JSON inspector — last-resort fallback. Auto-opens ONLY when
          both the structured renderer AND the narrative are empty (rare
          but possible when the agent emits a non-standard JSON shape
          with no _raw_text). Otherwise sits collapsed as a tool for
          deeper inspection. */}
      <RawOutputFallback output={output} initiallyOpen={richEmpty && !hasNarrative} />

      {summaryKeys.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Show raw summary fields
          </summary>
          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {summaryKeys.map((k) => (
              <div key={k} className="flex justify-between gap-3 border-b pb-1">
                <dt className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</dt>
                <dd className="font-medium text-right break-words">
                  {formatValue(fieldFrom(output, k))}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          View full agent output (JSON)
        </summary>
        <pre className="mt-2 max-h-72 overflow-auto rounded bg-muted p-3 text-xs">
          {JSON.stringify(output, null, 2)}
        </pre>
      </details>
    </>
  );
}

/**
 * Document Assistant produced no usable text — surface the fallback path
 * explicitly so the reviewer doesn't think they're stuck. The Document
 * Agent's text-source chain is document_assistant_result.text -> PyMuPDF
 * embedded text -> Kaycoo MCP ocr.extract_text -> hard error, so clicking
 * Approve here just hands off to the next fallback.
 */
export function DocAssistantFallbackNotice() {
  return (
    <Alert className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
      <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300" aria-hidden="true" />
      <AlertDescription className="text-sm text-amber-900 dark:text-amber-100">
        <span className="font-semibold">No transcription returned.</span>{' '}
        You can still continue — the Document Agent will fall back to
        PyMuPDF (embedded PDF text) and, if that fails, the Kaycoo MCP
        OCR tool. Click <em>Skip &amp; use fallback OCR</em> below to
        advance, or <em>Retry agent</em> to re-run the assistant once
        the platform-side fix is deployed.
      </AlertDescription>
    </Alert>
  );
}
